import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useUi } from '@/context/Ui';
import { useAssetCdn } from '@/context/Ui';
import { useLessonPlayer } from '@/context/LessonPlayer';
import { SymbolType, CdnObjectType } from '@/types';
import { AudioPlayer } from '@/components';
import { WhitePanel } from '../components/WhitePanel';
import { ActivityTitleStep } from '../components/ActivityTitleStep';
import { Activity, ActivityStep, ActivityStepProps } from '../Activity';
import { ActivityTypeProps } from '../types';
import { GrabGameSpell } from '../components/GrabGameSpell';

interface ManipulateSoundsStepProps extends ActivityStepProps {
  words: Array<string>;
}

const ManipulateSoundsStep = ({
  activity,
  mode,
  wrapperClassName,
  isPreview,
  stepNumber,
  totalSteps,
  words,
}: ManipulateSoundsStepProps) => {
  const playerRef = useRef<AudioPlayer>(null);

  const { sendAlert } = useUi();

  const {
    playerReady: playerContextReady,
    scrollNextSlide,
    getEncounteredLetters,
    getLessonByActivity,
    enablePlayButton,
    disablePlayButton,
    enableRefreshButton,
    disableRefreshButton,
  } = useLessonPlayer();

  const [playedOnce, setPlayedOnce] = useState(false);

  const [playerReady, setPlayerReady] = useState(false);

  const [currentWordIndex, setCurrentWordIndex] = useState(0);

  const [spelt, setSpelt] = useState<Array<string | undefined>>(
    Array.from({ length: words[currentWordIndex].length }),
  );

  const mediaUrl = useAssetCdn(
    words[currentWordIndex],
    SymbolType.Word,
    CdnObjectType.Audio,
  );

  const allowedLetters = useMemo(() => {
    if (!playerContextReady) {
      return;
    }

    const lesson = getLessonByActivity(activity);

    if (!lesson) {
      return;
    }

    return getEncounteredLetters(lesson.id);
  }, [
    playerContextReady,
    activity,
    getEncounteredLetters,
    getLessonByActivity,
  ]);

  useEffect(() => {
    if (currentWordIndex > 0) {
      playerRef.current?.play();
    }
  }, [currentWordIndex]);

  return (
    <ActivityStep
      key={`${activity.id}-1`}
      stepIdentifier={`${activity.id}-1`}
      activity={activity}
      mode={mode}
      stepNumber={stepNumber}
      totalSteps={totalSteps}
      wrapperClassName={wrapperClassName}
      isPreview={isPreview}
      usePanel
      title={`Click to hear a word. Manipulate the letters to make new words.`}
      showTitle={true}
      showActivityPagination={false}
      onSlide={() => {
        enablePlayButton({
          onClick: () => {
            playerRef.current?.play();
            setPlayedOnce(true);
          },
          disabled: !playerReady,
          hint: !playedOnce,
        });

        enableRefreshButton({
          onClick: () =>
            setSpelt(Array.from({ length: words[currentWordIndex].length })),
        });
      }}
      onBeforeLeave={() => {
        disablePlayButton();
        disableRefreshButton();

        return true;
      }}
    >
      <WhitePanel mode="basic" kind="fill" color="paper" gap={true}>
        <div className="flex flex-col gap-12 items-center">
          {allowedLetters ? (
            <GrabGameSpell
              word={words[currentWordIndex]}
              allWords={words}
              spelt={spelt}
              setSpelt={setSpelt}
              allowedLetters={allowedLetters}
              onCorrect={() => {
                if (currentWordIndex < words.length - 1) {
                  setCurrentWordIndex(currentWordIndex + 1);
                }

                if (currentWordIndex === words.length - 1) {
                  scrollNextSlide?.();
                }
              }}
            />
          ) : null}
        </div>
      </WhitePanel>

      <AudioPlayer
        ref={playerRef}
        src={mediaUrl || ''}
        onReadySrc={() => setPlayerReady(true)}
        onLoadTimeoutSrc={() =>
          sendAlert('We could not load some of the audio for this lesson.', {
            type: 'warning',
            title: 'There was a problem',
          })
        }
      />
    </ActivityStep>
  );
};

export const ReadingManipulatingSounds = ({
  activity,
  mode,
  wrapperClassName,
  isPreview,
}: ActivityTypeProps) => {
  if (!activity.words || !activity.words2) {
    throw new Error('Words are required for this activity');
  }

  const { words } = activity;

  return (
    <Activity
      steps={[
        <ActivityTitleStep
          key={`${activity.id}-title`}
          activity={activity}
          mode={mode}
          wrapperClassName={wrapperClassName}
          parrotType="tablet"
          isPreview={isPreview}
          index={0}
          total={2}
        />,
        <ManipulateSoundsStep
          key={`${activity.id}-1`}
          stepIdentifier={`${activity.id}-1`}
          activity={activity}
          mode={mode}
          stepNumber={2}
          totalSteps={2}
          isPreview={isPreview}
          words={words}
        />,
      ]}
    />
  );
};
